import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './styles/HeroSection.css';

import heroImage from '../../../assets/images/hero-section/Design-and-Deployment-home.png';
import recruiterImg from '../../../assets/images/hero-section/Technical-recruiter-home.png';
import colocationImg from '../../../assets/images/hero-section/Help-finding-ideal-colocation-home.png';
import auditImg from '../../../assets/images/hero-section/Network-Audit-home.png';
import supportImg from '../../../assets/images/hero-section/Technical-recruiter-home.png';
import strategyImg from '../../../assets/images/hero-section/Business-Strategy-home.png';

const TYPING_TEXTS = ['Consulting Partner', 'Cloud Expert', 'Security Specialist'];
const TYPING_SPEED = 110;
const DELETING_SPEED = 50;
const PAUSE_AFTER_TYPING = 1200;
const PAUSE_AFTER_DELETING = 500;

const HeroSection = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [typing, setTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    let timeout;
    setShowCursor(true);

    if (typing) {
      if (typed.length < TYPING_TEXTS[index].length) {
        timeout = setTimeout(() => {
          setTyped(TYPING_TEXTS[index].slice(0, typed.length + 1));
        }, TYPING_SPEED);
      } else {
        timeout = setTimeout(() => setTyping(false), PAUSE_AFTER_TYPING);
      }
    } else {
      if (typed.length > 0) {
        timeout = setTimeout(() => {
          setTyped(TYPING_TEXTS[index].slice(0, typed.length - 1));
        }, DELETING_SPEED);
      } else {
        timeout = setTimeout(() => {
          setIndex((idx) => (idx + 1) % TYPING_TEXTS.length);
          setTyping(true);
        }, PAUSE_AFTER_DELETING);
      }
    }

    return () => clearTimeout(timeout);
  }, [typed, typing, index]);

  useEffect(() => {
    let cursorTimeout;
    if (!typing && typed.length === 0) {
      cursorTimeout = setTimeout(() => setShowCursor(false), 100);
    }
    return () => clearTimeout(cursorTimeout);
  }, [typed, typing]);

  return (
    <section className="hero">
      <div className="hero__background"></div>
      <div className="hero__grid" aria-hidden="true"></div>
      <div className="hero__overlay"></div>
      <div className="hero__glow hero__glow--one"></div>
      <div className="hero__glow hero__glow--two"></div>

      <div className="hero__content">
        <motion.div
          className="hero__text"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          

          <h1 className="hero__title">
            <span className="hero__title-line hero__title-line--first">
              Your Trusted IT{' '}
              <span className="hero__title-typing-wrap">
                <span className="hero__title--highlight">
                  {typed}
                  {showCursor && <span className="hero__cursor">|</span>}
                </span>
              </span>
            </span>

            <span className="hero__title-line hero__title-line--second">
              for Leading Technologies.
            </span>
          </h1>

          <p className="hero__description">
            Expert Services for Networking, Cloud, Security, and IT Infrastructure
          </p>

          <div className="hero__actions">
            <button
              className="hero__cta-btn hero__cta-btn--primary"
              onClick={() => navigate('/contact')}
            >
              Get Started
              <svg
                className="hero__btn-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>

            <button
              className="hero__cta-btn hero__cta-btn--secondary"
              onClick={() => navigate('/services')}
            >
              Explore Services
            </button>
          </div>
        </motion.div>

        <motion.div
          className="hero-image-jumble"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
        >
          <div className="jumble-frame ji-1">
            <img src={heroImage} alt="IT design and deployment" className="jumble-img" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>

          <div className="jumble-frame ji-2">
            <img src={recruiterImg} alt="Technical recruiter" className="jumble-img" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>

          <div className="jumble-frame ji-3">
            <img src={colocationImg} alt="Colocation support" className="jumble-img" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>

          <div className="jumble-frame ji-4">
            <img src={auditImg} alt="Network audit" className="jumble-img" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>

          <div className="jumble-frame ji-5">
            <img src={supportImg} alt="Technical support" className="jumble-img" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>

          <div className="jumble-frame ji-6">
            <img src={strategyImg} alt="Business strategy" className="jumble-img" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>
        </motion.div>
      </div>

      <div className="hero__scroll-indicator">
        <span className="hero__scroll-label">Scroll</span>
        <div className="hero__scroll-line"></div>
      </div>
    </section>
  );
};

export default HeroSection;
